import resizeCanvas from '../util/resize-canvas';

/**
 * Canvas Sine Circles
 * inspired from canvas code by George Hastings http://georgehastings.com
 */
class CanvasLines {
  constructor() {
    this.canvas = document.querySelector('.js--canvas-circles');
    this.container = document.querySelector('.js--canvas-container');
    this.nav = document.querySelector('.js--ela-nav-list');
    this.navList = document.querySelectorAll('.ela-nav__list-item');
    this.ctx = this.canvas.getContext('2d');
    this.scale = window.devicePixelRatio || 1;
    this.circleArray = [];
    this.totalOffset = 0;
    this.frames = '';
    this.navIndex = 0;
    this.isHovered = false;

    this.circleCount = this.navList.length;
    this.circleOffset = this.container.offsetWidth / 60;
    this.ungulator = 1;
    this.wavelength = 1.6;
    this.poly = 100;
    this.color = {
      white: '#ffffff',
      red: '#ff4637',
    };

    this.canvas.width = this.container.offsetWidth;
    this.canvas.height = this.container.offsetWidth;

    this.step = (2 * Math.PI) / this.poly;
    this.length = 2 * Math.PI;
    this.center = {
      x: this.canvas.width / 2,
      y: this.canvas.height / 2,
    };

    this.animate;
    this.radius = this.canvas.width / 3.25;

    this.dataSine = [];
    this.count = 0;
    this.startingTheta = 0;
    this.initialize();
  }

  /**
   * Initialize and bind events
   */
  initialize() {
    window.addEventListener('resize', this.onResize.bind(this));
    this.nav.addEventListener('mouseenter', this.onMouseEnter.bind(this));
    this.nav.addEventListener('mouseover', this.onMouseOver.bind(this));
    this.nav.addEventListener('mouseleave', this.onMouseLeave.bind(this));
    this.initCanvas();
  }

  /**
   * Create and setup the canvas
   */
  initCanvas() {
    this.onResize();
    this.sortPositions();
    this.render();
  }

  /**
   * Window resize handler
   */
  onResize() {
    this.resizeCanvas();
  }

  /**
   * Mouse enter handler
   */
  onMouseEnter() {
    this.isHovered = true;
  }

  /**
   * Mouse over handler
   */
  onMouseOver(e) {
    const parent = this.navList;
    const child = e.target.parentNode;
    const index = [...parent].indexOf(child);
    this.navIndex = this.circleArray[index];
  }

  /**
   * Mouse leave handler
   */
  onMouseLeave() {
    this.isHovered = false;
  }

  /**
   * Resizes, scales, and resets width of the canvas element
   */
  resizeCanvas() {
    resizeCanvas(this.scale, this.container, this.canvas, this.ctx);

    if (window.innerWidth > 768) {
      this.center = {
        x: this.container.offsetWidth / 2,
        y: this.container.offsetHeight / 2,
      };
      this.circleOffset = this.container.offsetWidth / 60;
      this.radius = this.container.offsetWidth / 3.25;
    } else {
      this.center = {
        x: 0,
        y: this.container.offsetHeight / 2,
      };
      this.circleOffset = this.container.offsetWidth / 30;
      this.radius = this.container.offsetWidth / 2.25;
    }
  }

  /**
   * Pushes and sorts circle positions in order to map them to the nav
   */
  sortPositions() {
    this.circleArray = [];
    for (let index = 0; index < this.circleCount; index++) {
      const x = this.getPosition(index, this.circleOffset);
      this.circleArray.push(x);
    }
    this.circleArray.sort((a, b) => a - b);
    this.circleArray.reverse();
  }

  /**
   * Centers a group of offset circles around a center point on the x axis
   * @param {Number} index current iterator
   * @param {Number} offset circle offset amount
   */
  getPosition(index, offset) {
    return (index % 2 === 0 ? 1 : -1) * (offset * Math.round(index / 2));
  }

  /**
   * Animate and render the sine circles
   */
  render() {
    this.frames = requestAnimationFrame(this.animate.bind(this));
  }

  /**
   * Recursively call the animate method
   */
  animate() {
    this.dataSine.length = 0;

    this.ctx.clearRect(0, 0, this.container.offsetWidth, this.container.offsetHeight);

    for (let index = 0; index < this.circleCount; index++) {
      const newIndex = index + 1;
      const rotation = (this.count + newIndex * 20) * 0.04;
      const dataSine = [];

      // Sets the offset positions for the circles
      const xPos = this.getPosition(index, this.circleOffset);
      this.sortPositions();

      // Animates sine wave around a circle
      for (let i = 0; i < this.poly; i++) {
        dataSine.push(Math.sin(i / this.wavelength + rotation) * this.ungulator);
      }

      this.ctx.beginPath();
      this.ctx.lineWidth = 1.5;
      this.ctx.strokeStyle = '#ffffff';

      // changes color of the circle based on currently hovered nav index
      if (this.isHovered && this.navIndex === xPos) {
        this.ctx.strokeStyle = this.color.red;
      } else {
        this.ctx.strokeStyle = this.color.white;
      }

      // circle circumference
      for (let theta = this.startingTheta; theta < this.length; theta += this.step) {
        const point = dataSine[(theta / this.step).toFixed(0)];
        const x = this.center.x + xPos + (this.radius + point) * Math.cos(theta);
        const y = this.center.y + (this.radius + point) * Math.sin(theta);
        this.ctx.lineTo(x, y);
      }

      this.ctx.closePath();
      this.ctx.stroke();
    }

    this.count++;
    this.frames = requestAnimationFrame(this.animate.bind(this));
  }
}

export default CanvasLines;
